import React from "react";
import { Container, Heading, SimpleGrid, Text, VStack } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FiUserCheck, FiBarChart2, FiClipboard } from "react-icons/fi";
import Feature from "./Feature";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";

const MotionVStack = motion(VStack);
const MotionHeading = motion(Heading);
const MotionText = motion(Text);
const MotionSimpleGrid = motion(SimpleGrid);

const featuresData = [
  {
    icon: FiUserCheck,
    iconBg: "#1F7CFF",
    heading: "Profile Builder",
    text: "Craft a job-ready profile with smart prompts and recruiter-approved sections",
  },
  {
    icon: FiBarChart2,
    iconBg: "#F75C4E",
    heading: "Skill Insights",
    text: "See which skills stand out in your field — highlight what really matters",
  },
  {
    icon: FiClipboard,
    iconBg: "#5C4EF7",
    heading: "Application Tracker",
    text: "Easily manage where you've applied and follow up like a pro",
  },
];

function Features() {
  const { ref, isInView } = useScrollAnimation({ once: true, margin: "-100px" });

  return (
    <Container maxW="6xl" pt={{ base: "2rem", md: "3rem" }} pb={{ base: "2rem", md: "3rem" }} px={{ base: 4, md: 8 }}>
      <MotionVStack
        ref={ref}
        spacing={{ base: "2rem", md: "2.5rem" }}
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Heading */}
        <VStack spacing="1.5rem" textAlign="center">
          <MotionHeading
            as="h2"
            size="xl"
            color="gray.900"
            fontWeight="700"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Boost Your Profile, Land Jobs
          </MotionHeading>
          <MotionText
            color="gray.600"
            maxW="600px"
            fontSize="lg"
            lineHeight="1.6"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            We researched top employer needs and built tools to help you stand out and get hired.
          </MotionText>
        </VStack>

        {/* Grid */}
        <MotionSimpleGrid
          columns={{ base: 1, md: 2, lg: 3 }}
          spacing={{ base: "2rem", md: "3rem" }}
          maxW="1200px"
          w="full"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {featuresData.map((featureData, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              transition={{
                duration: 0.5,
                delay: 0.5 + index * 0.1,
                ease: "easeOut"
              }}
            >
              <Feature
                icon={featureData.icon}
                iconBg={featureData.iconBg}
                heading={featureData.heading}
                text={featureData.text}
              />
            </motion.div>
          ))}
        </MotionSimpleGrid>
      </MotionVStack>
    </Container>
  );
}

export default Features;
